import { LineWeight } from '@mui/icons-material';
import { Button, Card, Divider, Icon, Loader, Pagination, Popup, Table, VerticalMenu } from '@scuf/common';
import { HttpResponseArquivo } from 'axios-http';
import Alert from 'components/alert';
import { formatDate } from 'helpers';
import requestFile from 'helpers/requestFile';
import { WorkOrderRmaLiteDto, WorkOrderPaginatedDto } from 'models';
import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { useSetRecoilState } from 'recoil';
import { REPAIR_CENTER, notificationAtom } from 'resources';
import { getReportOsFile, getWorkOrders } from 'resources/api/';

interface Props {
    isOpen: boolean;
}

const ServiceOrdersAccordion: React.FC<Props> = ({ isOpen }) => {
    const { t } = useTranslation(['common', 'repaircenter']);
    const navigate = useNavigate();
    const [rmaDetail, setRmaDetail] = useState<WorkOrderPaginatedDto | null>(null);
    const [reportSelected, setReportSelected] = useState<string | null>(null);
    const [isOsLoading, setIsOsLoading] = useState(false);
    const [loading, setLoading] = useState<boolean>(false);
    const setNotification = useSetRecoilState(notificationAtom);

    //VariaveisPaginacao
    const [paginaAtual, setPaginaAtual] = useState<number>(1);
    const [qtdPorPagina, setQtdPorPagina] = useState<number>(20);
    const [rmaNumber, setRmaNumber] = useState<number | null>(null);
    const [errorNotification, setErrorNotification] = useState(false);
    const [ready, setReady] = useState(false);

    useEffect(() => {
        const path = window.location.pathname;
        // /repair-center/rma-detail/54575
        const parts = path.split('/');
        const rma = parts[parts.length - 1];
        setRmaNumber(Number(rma));
    }, []);

    useEffect(() => {
        if (!isOpen) return;
        const fetchData = async () => {
            setErrorNotification(false);
            setIsOsLoading(true);
            if (rmaNumber == null) {
                setIsOsLoading(false);
                return;
            }
            try {
                const response = await getWorkOrders(rmaNumber, paginaAtual, qtdPorPagina);
                if (!response.error) {
                    setRmaDetail(response.data);
                } else if (response.error) {
                    setErrorNotification(true);
                }
            } catch (error) {
                setErrorNotification(true);
            } finally {
                setIsOsLoading(false);
            }
        };
        fetchData();
    }, [rmaNumber, paginaAtual, qtdPorPagina, isOpen]);

    const pendingAprv = (rma: WorkOrderRmaLiteDto): boolean => {
        return (
            rma.status !== 'ABERTO' &&
            rma.status !== 'EM TRIAGEM' &&
            rma.status !== 'EM ANALISE' &&
            rma.status !== 'AGUARDANDO CADASTRO' &&
            rma.status !== 'EMITIR ORÇAMENTO' &&
            rma.status !== 'ORÇAMENTO EMITIDO'
        );
    };

    const handlePrint = async (workOrder: string): Promise<void> => {
        setReportSelected(workOrder);
        await requestFile.print(REPAIR_CENTER.getTechnicalReportHTML, {
            method: 'POST',
            params: { ids: [workOrder] }
        });
        setReportSelected(null);
    };

    const handleExportXlxs = async (): Promise<void> => {
        if (!loading && rmaNumber) {
            try {
                setLoading(true);
                const response = await getReportOsFile(rmaNumber);
                await downloadReportFile(response);
            } catch (e) {
                setNotification(old => ({
                    ...old,
                    message: 'ErrorDownloadingFile',
                    type: 'critical'
                }));
            } finally {
                setLoading(false);
            }
        }
    };

    async function downloadReportFile(reportData: HttpResponseArquivo) {
        if (reportData.status != 200) {
            setNotification(old => ({ ...old, message: 'NoResult', type: 'information' }));
            setLoading(false);
        } else {
            const blob = new Blob([reportData.data], {
                type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet'
            });
            const url = URL.createObjectURL(blob);
            const link = document.createElement('a');
            link.href = url;
            link.download = `report${'Os'}`;
            link.style.display = 'none';
            document.body.appendChild(link);
            link.click();
            document.body.removeChild(link);
            URL.revokeObjectURL(url);
        }
    }

    const handleTechnicalReport = async (): Promise<void> => {
        const allReports = rmaDetail?.workOrders?.filter(e => e.relatorio).map(e => e.os);
        await requestFile.print(REPAIR_CENTER.getTechnicalReportHTML, {
            method: 'POST',
            params: { ids: allReports }
        });
    };

    useEffect(() => {
        if (isOpen && rmaDetail) {
            const timer = setTimeout(() => {
                setReady(true);
            }, 150);

            return () => clearTimeout(timer);
        } else {
            setReady(false);
        }
    }, [isOpen, rmaDetail]);

    return !rmaDetail?.workOrders && !isOsLoading ? (
        <Alert text={t('notification:information.NoData')} />
    ) : (
        <div>
            <Alert text={t('repaircenter:translation.AlertOS')} />
            {rmaDetail !== null ? (
                <div className="container-search-os">
                    <Popup
                        className="popup-theme-wrap"
                        element={
                            <Button
                                type="link"
                                content={t('actions.Export')}
                                icon={<Icon name="ellipsis-vertical" root="common" size="large" />}
                            />
                        }
                        on="click"
                    >
                        <VerticalMenu>
                            <VerticalMenu.Item onClick={handleExportXlxs}>{t('actions.ExportXLSX')}</VerticalMenu.Item>
                            {rmaDetail?.workOrders?.find(e => e.relatorio) && (
                                <VerticalMenu.Item onClick={handleTechnicalReport}>{t('actions.ExportReport')}</VerticalMenu.Item>
                            )}
                        </VerticalMenu>
                    </Popup>
                    <div className="div-datatable">
                        <div>
                            {ready && (
                                <Pagination
                                    showDisplayDetails={true}
                                    expandEllipsis={true}
                                    totalItems={rmaDetail.quantidadeTotal}
                                    itemsPerPage={qtdPorPagina}
                                    boundaryRange={5}
                                    itemsPerPageOptions={[10, 20, 50, 100]}
                                    onPageChange={(activePage, itemsPerPage) => {
                                        setPaginaAtual(Number(activePage));
                                        setQtdPorPagina(Number(itemsPerPage));
                                    }}
                                    activePage={paginaAtual}
                                />
                            )}
                            <Table>
                                <Table.Header>
                                    <Table.HeaderCell content={t('repaircenter:rma.service_orders.Number')} />
                                    <Table.HeaderCell content={t('repaircenter:rma.service_orders.Model')} />
                                    <Table.HeaderCell content={t('repaircenter:rma.service_orders.Modality')} />
                                    <Table.HeaderCell content={t('SN')} />
                                    <Table.HeaderCell content={t('translation.Status')} />
                                    <Table.HeaderCell content={t('status.SituationClient')} />
                                    <Table.HeaderCell content={t('repaircenter:rma.service_orders.DateIn')} />
                                    <Table.HeaderCell content={t('repaircenter:rma.service_orders.DateOut')} />
                                    <Table.HeaderCell content={t('translation.Code')} />
                                    <Table.HeaderCell content={t('translation.Report')} />
                                </Table.Header>
                                <Table.Body>
                                    {errorNotification && !isOsLoading ? (
                                        <Table.Row>
                                            <Table.Cell colSpan={10} style={{ padding: 0 }}>
                                                <div style={{ position: 'relative', height: '120px' }}>
                                                    <div style={{ backgroundColor: '#fac8c8' }}>
                                                        Erro ao realizar consulta, diminuia a quantidade de itens por página!
                                                    </div>
                                                </div>
                                            </Table.Cell>
                                        </Table.Row>
                                    ) : null}
                                    {isOsLoading ? (
                                        <Table.Row>
                                            <Table.Cell colSpan={10} style={{ padding: 0 }}>
                                                <div style={{ position: 'relative', height: '120px' }}>
                                                    {/* Overlay ocupa toda a linha */}
                                                    <Loader overlayColor="#f3f3f3" />
                                                    {/* Conteúdo centralizado */}
                                                    <div
                                                        style={{
                                                            position: 'absolute',
                                                            top: 0,
                                                            left: 0,
                                                            right: 0,
                                                            bottom: 0,
                                                            display: 'flex',
                                                            justifyContent: 'center',
                                                            alignItems: 'center'
                                                        }}
                                                    >
                                                        <div>Loading</div>
                                                    </div>
                                                </div>
                                            </Table.Cell>
                                        </Table.Row>
                                    ) : rmaDetail !== null && !errorNotification ? (
                                        [
                                            rmaDetail?.workOrders?.map((rma: WorkOrderRmaLiteDto, index: number) => (
                                                <Table.Row key={index}>
                                                    <Table.Cell
                                                        onClick={() =>
                                                            pendingAprv(rma)
                                                                ? navigate('budget', {
                                                                      state: {
                                                                          data: {
                                                                              id: rma?.os,
                                                                              rmaId: rma?.rma,
                                                                              flagRma: rma?.flagApproved
                                                                          }
                                                                      }
                                                                  })
                                                                : null
                                                        }
                                                        className={pendingAprv(rma) ? 'clickable' : ''}
                                                    >
                                                        {rma?.os}
                                                    </Table.Cell>
                                                    <Table.Cell>{rma?.partnumber} </Table.Cell>
                                                    <Table.Cell>{rma?.modalidade} </Table.Cell>
                                                    <Table.Cell>{rma?.serial} </Table.Cell>
                                                    <Table.Cell>{rma?.status}</Table.Cell>
                                                    <Table.Cell>
                                                        {rma?.flagApproved === 1
                                                            ? 'Aprovado'
                                                            : rma?.flagApproved === 0
                                                              ? 'Reprovado'
                                                              : rma?.status !== 'Aguardando Aprovação'
                                                                ? '-'
                                                                : rma?.status}
                                                    </Table.Cell>
                                                    <Table.Cell>{formatDate(rma?.dataEntrada)} </Table.Cell>
                                                    <Table.Cell>
                                                        {formatDate(rma?.dataSaida) === '1/1/1'
                                                            ? ''
                                                            : formatDate(rma?.dataSaida)}{' '}
                                                    </Table.Cell>
                                                    <Table.Cell>
                                                        {rma?.linkRastreio ? (
                                                            <a
                                                                href={rma.linkRastreio}
                                                                target="_blank"
                                                                rel="noopener noreferrer"
                                                                className="text-blue-600 underline"
                                                            >
                                                                RASTREAR
                                                            </a>
                                                        ) : (
                                                            t('common:status.NotInformed')
                                                        )}
                                                    </Table.Cell>
                                                    {(rma.relatorio && (
                                                        <Table.Cell>
                                                            {rma?.os === reportSelected ? (
                                                                <Icon loading={true} name="refresh" />
                                                            ) : (
                                                                <Icon
                                                                    style={{ cursor: 'pointer' }}
                                                                    root="common"
                                                                    name="document-report"
                                                                    size="medium"
                                                                    onClick={() => handlePrint(rma?.os)}
                                                                />
                                                            )}
                                                        </Table.Cell>
                                                    )) || <Icon name="unavailable" />}
                                                </Table.Row>
                                            ))
                                        ]
                                    ) : (
                                        <Table.Row></Table.Row>
                                    )}
                                </Table.Body>
                            </Table>
                        </div>
                    </div>
                </div>
            ) : (
                <div>
                    <Table>
                        <Table.Header>
                            <Table.HeaderCell content={t('repaircenter:rma.service_orders.Number')} />
                            <Table.HeaderCell content={t('repaircenter:rma.service_orders.Model')} />
                            <Table.HeaderCell content={t('repaircenter:rma.service_orders.Modality')} />
                            <Table.HeaderCell content={t('SN')} />
                            <Table.HeaderCell content={t('translation.Status')} />
                            <Table.HeaderCell content={t('status.SituationClient')} />
                            <Table.HeaderCell content={t('repaircenter:rma.service_orders.DateIn')} />
                            <Table.HeaderCell content={t('repaircenter:rma.service_orders.DateOut')} />
                            <Table.HeaderCell content={t('translation.Code')} />
                            <Table.HeaderCell content={t('translation.Report')} />
                        </Table.Header>
                        <Table.Row>
                            <Table.Cell colSpan={10} style={{ padding: 0 }}>
                                <div style={{ position: 'relative', height: '120px' }}>
                                    {/* Overlay ocupa toda a linha */}
                                    <Loader overlayColor="#f3f3f3" />
                                    {/* Conteúdo centralizado */}
                                    <div
                                        style={{
                                            position: 'absolute',
                                            top: 0,
                                            left: 0,
                                            right: 0,
                                            bottom: 0,
                                            display: 'flex',
                                            justifyContent: 'center',
                                            alignItems: 'center'
                                        }}
                                    >
                                        <div>Loading</div>
                                    </div>
                                </div>
                            </Table.Cell>
                        </Table.Row>
                    </Table>
                </div>
            )}
        </div>
    );
};

export default ServiceOrdersAccordion;
